// Generic selection dialog for the menu's settings (backgrounds, languages): a scrimmed
// panel with one visual card per option. Rendered in the #overlay HTML layer (above the
// Phaser canvas AND Phaser's DOM layer, so the menu pills can't float over it), with the
// device Back gesture wired to close it — same pattern as the privacy viewer.
import * as overlay from './overlay';

export interface PickerChoice {
  id: string;         // value handed to onSelect
  tileHtml: string;   // visual for the card's tile area (an <img>, inline <svg>, flag span, …)
  name: string;       // caption under the tile
  badge?: string;     // optional second line under the name (the lesson picker's "★ 12/25")
  done?: boolean;     // optional "finished" styling — a gold frame, distinct from --selected
  age?: number;       // optional "8+" corner sticker — an age this lesson expects (no translation)
}

let popHandler: ((e: PopStateEvent) => void) | null = null;
let open = false;

/**
 * Closes the picker if one is open. MenuScene calls this on every create(): a language
 * switch restarts the scene only after its fonts finish loading, so a dialog opened from
 * the outgoing scene during that window would otherwise linger with stale options.
 */
export function closeChoicePicker(): void {
  if (open) close({ fromPop: false });
}

/**
 * Opens the dialog. `onSelect` fires (then the dialog closes) only when a card is
 * tapped — closing via ✕ / scrim / Back leaves the current choice untouched.
 */
export function openChoicePicker(
  title: string,
  choices: PickerChoice[],
  selected: string,
  onSelect: (id: string) => void,
): void {
  const cards = choices.map((c) => `
    <button type="button" class="picker__card${c.id === selected ? ' picker__card--selected' : ''}${c.done ? ' picker__card--done' : ''}"
            data-choice="${c.id}" aria-pressed="${c.id === selected}">
      ${c.age ? `<span class="picker__age" aria-hidden="true">${c.age}+</span>` : ''}
      <span class="picker__tile">${c.tileHtml}</span>
      <span class="picker__name">${c.name}</span>
      ${c.badge ? `<span class="picker__badge">${c.badge}</span>` : ''}
      <span class="picker__check" aria-hidden="true">✓</span>
    </button>`).join('');

  overlay.show(`
    <div class="picker" id="picker-scrim">
      <div class="picker__panel" role="dialog" aria-label="${title}">
        <div class="picker__head">
          <span class="picker__title">${title}</span>
          <button type="button" id="picker-close" class="picker__close" aria-label="Close">✕</button>
        </div>
        <div class="picker__grid">${cards}</div>
      </div>
    </div>
  `);

  open = true;

  // Hardware/gesture Back closes the dialog instead of leaving the app (TWA/WKWebView).
  history.pushState({ picker: true }, '');
  popHandler = () => close({ fromPop: true });
  window.addEventListener('popstate', popHandler);

  document.getElementById('picker-close')
    ?.addEventListener('click', () => close({ fromPop: false }));
  const scrim = document.getElementById('picker-scrim');
  scrim?.addEventListener('click', (e) => {
    if (e.target === scrim) close({ fromPop: false });   // only the backdrop itself, not the panel
  });
  scrim?.querySelectorAll<HTMLButtonElement>('[data-choice]').forEach((card) => {
    card.addEventListener('click', () => {
      onSelect(card.dataset.choice!);
      close({ fromPop: false });
    });
  });
}

// fromPop: the close was triggered by a popstate (Back) — history already moved,
// so we must NOT call history.back() again. Otherwise pop our pushed entry.
function close({ fromPop }: { fromPop: boolean }): void {
  open = false;
  if (popHandler) {
    window.removeEventListener('popstate', popHandler);
    popHandler = null;
  }
  overlay.hide();
  if (!fromPop && history.state?.picker) history.back();
}
